import React from 'react';
import { Container, Text, Button, Card } from "@nextui-org/react";
import { useParams } from "react-router-dom";
import stateMachineDemo from "../../../config/stateMachineDemo.json";
import { Printer } from "lucide-react";

const WorkshopMarkdownPage = () => {
    const { workshopId } = useParams();

    // Type the imported JSON. We know it matches the schema from WorkshopSteps
    const config = stateMachineDemo as Record<string, any>;

    const handlePrint = () => {
        window.print();
    };

    // Helper to format the markdown output
    const renderMarkdownContent = () => {
        return Object.entries(config).map(([stepName, stepData], index) => {
            if (stepData === null) {
                return (
                    <div key={stepName} className="mb-6">
                        <Text h3 css={{ margin: 0 }}>{index + 1}. {stepName}</Text>
                        <Text css={{ marginLeft: '$8', marginTop: '$2' }}>
                            <strong>Type:</strong> End State<br />
                            <em>End of workflow.</em>
                        </Text>
                    </div>
                );
            }

            if (typeof stepData === 'string') {
                return (
                    <div key={stepName} className="mb-6">
                        <Text h3 css={{ margin: 0 }}>{index + 1}. {stepName}</Text>
                        <Text css={{ marginLeft: '$8', marginTop: '$2' }}>
                            <strong>Type:</strong> Simple Transition<br />
                            <strong>Next State:</strong> {stepData}
                        </Text>
                    </div>
                );
            }

            // Branching
            if ('variable' in stepData) {
                return (
                    <div key={stepName} className="mb-6">
                        <Text h3 css={{ margin: 0 }}>{index + 1}. {stepName}</Text>
                        <Text css={{ marginLeft: '$8', marginTop: '$2' }}>
                            <strong>Type:</strong> Branching Logic<br />
                            <strong>Variable:</strong> {stepData.variable}<br />
                            <strong>Default Next State:</strong> {stepData.default}
                        </Text>
                        <Text css={{ marginLeft: '$8', marginTop: '$4', fontWeight: 'bold' }}>Options:</Text>
                        <ul style={{ marginLeft: '40px', marginTop: '4px', listStyleType: 'disc' }}>
                            {Object.entries(stepData.options).map(([key, val]) => (
                                <li key={key}>If <strong>{key}</strong> → {val as string}</li>
                            ))}
                        </ul>
                    </div>
                );
            }

            // Complex States
            if ('stateType' in stepData) {
                switch(stepData.stateType) {
                    case 'video':
                        return (
                            <div key={stepName} className="mb-6">
                                <Text h3 css={{ margin: 0 }}>{index + 1}. {stepName}</Text>
                                <Text css={{ marginLeft: '$8', marginTop: '$2' }}>
                                    <strong>Type:</strong> Video<br />
                                    <strong>Title Key:</strong> {stepData.titleKey}<br />
                                    <strong>Next State:</strong> {stepData.nextState}
                                </Text>
                                <Text css={{ marginLeft: '$8', marginTop: '$4', fontWeight: 'bold' }}>Videos:</Text>
                                <ul style={{ marginLeft: '40px', marginTop: '4px', listStyleType: 'none', paddingLeft: 0 }}>
                                    {Object.entries(stepData.videos).map(([lang, data]) => {
                                        if (typeof data === 'string') {
                                            return <li key={lang} className="mb-2"><strong>{lang}:</strong> {data}</li>;
                                        }
                                        const videoData = data as { videoId: string, playbackId: string };
                                        return (
                                            <li key={lang} className="mb-2">
                                                <strong>{lang}:</strong>
                                                <ul style={{ marginLeft: '20px', listStyleType: 'circle' }}>
                                                    <li>Video ID: {videoData.videoId}</li>
                                                    <li>Playback ID: {videoData.playbackId}</li>
                                                </ul>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    case 'survey':
                        return (
                            <div key={stepName} className="mb-6">
                                <Text h3 css={{ margin: 0 }}>{index + 1}. {stepName}</Text>
                                <Text css={{ marginLeft: '$8', marginTop: '$2' }}>
                                    <strong>Type:</strong> Survey<br />
                                    <strong>Survey ID:</strong> {stepData.surveyId}<br />
                                    <strong>Next State:</strong> {stepData.nextState}
                                </Text>
                            </div>
                        );
                    case 'videoConference':
                        return (
                            <div key={stepName} className="mb-6">
                                <Text h3 css={{ margin: 0 }}>{index + 1}. {stepName}</Text>
                                <Text css={{ marginLeft: '$8', marginTop: '$2' }}>
                                    <strong>Type:</strong> Video Conference<br />
                                    <strong>Next State:</strong> {stepData.nextState}
                                </Text>
                                <Text css={{ marginLeft: '$8', marginTop: '$4', fontWeight: 'bold' }}>URLs:</Text>
                                <ul style={{ marginLeft: '40px', marginTop: '4px', listStyleType: 'disc' }}>
                                    {stepData.Hyperbeam?.urls?.map((url: string, i: number) => (
                                        <li key={i}><a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{url}</a></li>
                                    ))}
                                </ul>
                            </div>
                        );
                    default:
                        return (
                            <div key={stepName} className="mb-6">
                                <Text h3 css={{ margin: 0 }}>{index + 1}. {stepName}</Text>
                                <Text css={{ marginLeft: '$8', marginTop: '$2' }}>
                                    <strong>Type:</strong> Unknown ({stepData.stateType})
                                </Text>
                            </div>
                        );
                }
            }

            return null;
        });
    };

    return (
        <Container display="flex" direction="column" css={{ minHeight: "100vh", padding: "40px", backgroundColor: "#fff" }}>
            {/* Header section (hidden when printing) */}
            <div className="print:hidden" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <Text h2>Workshop Workflow: {workshopId}</Text>
                <Button
                    auto
                    color="primary"
                    icon={<Printer size={18} />}
                    onClick={handlePrint}
                >
                    Print Document
                </Button>
            </div>

            {/* Print specific styles and the document content */}
            <style>
                {`
                    @media print {
                        body {
                            background-color: white !important;
                        }
                        .print\\:hidden {
                            display: none !important;
                        }
                        .markdown-container {
                            font-family: serif;
                            padding: 0;
                            margin: 0;
                            color: black;
                        }
                        .markdown-container h1 {
                            font-size: 24pt;
                            margin-bottom: 20pt;
                            border-bottom: 1pt solid #ccc;
                            padding-bottom: 10pt;
                        }
                        .markdown-container h3 {
                            font-size: 16pt;
                            margin-top: 20pt;
                            margin-bottom: 10pt;
                            color: #333;
                        }
                        .markdown-container p, .markdown-container li {
                            font-size: 12pt;
                            line-height: 1.5;
                        }
                    }
                `}
            </style>

            <div className="markdown-container" style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                <div className="hidden print:block mb-8 border-b pb-4">
                    <Text h1 css={{ margin: 0 }}>Workshop Workflow Document</Text>
                    <Text css={{ marginTop: '$4', color: '$accents6' }}>Workshop ID: {workshopId}</Text>
                </div>

                <Card css={{ padding: '$10', '@media print': { border: 'none', padding: 0, boxShadow: 'none' } }}>
                    {renderMarkdownContent()}
                </Card>
            </div>
        </Container>
    );
};

export default WorkshopMarkdownPage;
